<script setup lang="ts">
    import { computed, onMounted, watch, ref, type Ref } from 'vue'
    import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router'
    import { EditorState, generateName } from '@common/Common'
    import CorporateNavigation from '@common/Components/CorporateNavigation.vue'
    import { guid } from '@common/Guid'
    import { CurrencyDenomination, MonthYear, MonthYearHelpers, ValidationError } from '@common/types'
    import { useExpenseMetricConfigurationQueryV1 } from '@Quest/Metrics/queries-vue-composables'
    import { useCreateExpenseMetricCommandV1, useUpdateExpenseMetricCommandV1 } from '@Quest/Metrics/commands-vue-composables'
    import type { ExpenseMetricConfigurationReadModel } from '@Quest/Metrics/readmodels'
    import { ValueBalanceType, ValueCommitmentType } from '@Quest/Metrics/types'
    import MetricTimeWindowSettings from '@Quest/Metrics/Components/MetricTimeWindowSettings.vue'
    import MetricScheduleDependentSettings from '@Quest/Metrics/Components/MetricScheduleDependentSettings.vue'

    const route = useRoute()
    const router = useRouter()

    const workspaceId = computed(() => Number(route.params?.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params?.strategyId ?? ''))
    const pageMode = computed(() => route.meta.mode as EditorState)
    const isEditMode = computed(() => pageMode.value === EditorState.Editor)

    const {
        runExpenseMetricConfigurationQueryV1,
        modelExpenseMetricConfigurationQueryV1
    } = useExpenseMetricConfigurationQueryV1(workspaceId, strategyId)

    const rm = modelExpenseMetricConfigurationQueryV1 as Ref<ExpenseMetricConfigurationReadModel | null>

    const {
        invokeCreateExpenseMetricCommandV1,
        validationErrorsCreateExpenseMetricCommandV1,
        onSucceedCreateExpenseMetricCommandV1
    } = useCreateExpenseMetricCommandV1(workspaceId, strategyId)

    const {
        invokeUpdateExpenseMetricCommandV1,
        validationErrorsUpdateExpenseMetricCommandV1,
        onSucceedUpdateExpenseMetricCommandV1
    } = useUpdateExpenseMetricCommandV1(workspaceId, strategyId)


    const initialized = ref(false)
    const errors = ref<ValidationError[]>([])
    const isSaving = ref(false)
    const allowRouteLeave = ref(false)

    const name = ref<string | null>(null)
    const description = ref<string | null>(null)
    const expenseAccountId = ref<string | null>(null)
    const denomination = ref<CurrencyDenomination>(CurrencyDenomination.Unit)
    const windowStartOffsetInMonths = ref<number | null>(null)
    const windowLengthInMonths = ref<number | null>(null)

    const valueBalanceType = ref<ValueBalanceType>(ValueBalanceType.Costs)

    const isScheduleDependent = ref(false)
    const applyBenefitsConfidenceFactor = ref(false)
    const valueCommitmentType = ref<ValueCommitmentType>(ValueCommitmentType.Total)

    const defaultProjectStartDate = ref<MonthYear | null>(null)
    const startDateBoundary = ref<MonthYear | null>(null)
    const endDateBoundary = ref<MonthYear | null>(null)

    const pageHeading = computed(() => isEditMode.value ? "Edit the metric" : "New expense metric")
    const saveButtonText = computed(() => isSaving.value ? "Saving..." : "Save changes")
    const nameError = computed(() => errors.value.find(e => e.ErrorCode.startsWith("Name."))?.Error ?? null)

    const hasChanges = computed(() =>
    {
        if (!isEditMode.value) return true

        const model = rm.value
        if (!model) return true

        const scheduleEnabled = isScheduleDependent.value
        const scheduleExists = model.ScheduleDependentSettings != null

        if (scheduleEnabled !== scheduleExists)
            return true

        // base fields (common)
        if (
            name.value !== model.Name ||
            description.value !== model.Description ||
            expenseAccountId.value !== model.AccountId ||
            denomination.value !== model.Denomination ||
            windowStartOffsetInMonths.value !== model.WindowStartOffsetInMonths ||
            windowLengthInMonths.value !== model.WindowLengthInMonths
        )
        {
            return true
        }

        if (!scheduleEnabled)
            return false

        const s = model.ScheduleDependentSettings

        return (
            applyBenefitsConfidenceFactor.value !== s?.ApplyBenefitsConfidenceFactor ||
            valueCommitmentType.value !== s?.ValueCommitmentType ||
            !MonthYearHelpers.areEqual(defaultProjectStartDate.value, s?.DefaultProjectStartDate) ||
            !MonthYearHelpers.areEqual(startDateBoundary.value, s?.StartDateBoundary) ||
            !MonthYearHelpers.areEqual(endDateBoundary.value, s?.EndDateBoundary)
        )
    })

    function handleBack()
    {
        router.go(-1)
    }

    function handleCancel()
    {
        if (isEditMode.value) router.go(0)
        else router.go(-1)
    }

    function handleSave()
    {
        if (!rm.value) return

        isSaving.value = true
        errors.value = []

        const payload = {
            id: isEditMode.value ? rm.value.Id : guid(),
            name: name.value,
            description: description.value,
            expenseAccountId: expenseAccountId.value,
            valueBalanceType: valueBalanceType.value,
            denomination: denomination.value,
            windowStartOffsetInMonths: windowStartOffsetInMonths.value,
            windowLengthInMonths: windowLengthInMonths.value,
            isScheduleDependent: isScheduleDependent.value,
            defaultProjectStartDate: defaultProjectStartDate.value,
            startDateBoundary: startDateBoundary.value,
            endDateBoundary: endDateBoundary.value,
            valueCommitmentType: valueCommitmentType.value,
            applyBenefitsConfidenceFactor: applyBenefitsConfidenceFactor.value
        }

        if (isEditMode.value)
            invokeUpdateExpenseMetricCommandV1(payload)
        else
            invokeCreateExpenseMetricCommandV1(payload)
    }

    onMounted(() =>
    {
        document.title = "Expense Metric Configuration"
        runExpenseMetricConfigurationQueryV1({ MetricId: route.params.metricId ?? null })
    })

    watch(rm, (model) =>
    {
        if (!model || initialized.value) return

        name.value = isEditMode.value ? model.Name : generateName(model.Name)

        description.value = model.Description
        expenseAccountId.value = model.AccountId ?? null
        denomination.value = model.Denomination
        valueBalanceType.value = model.ValueBalanceType
        windowStartOffsetInMonths.value = model.WindowStartOffsetInMonths
        windowLengthInMonths.value = model.WindowLengthInMonths

        isScheduleDependent.value = model.ScheduleDependentSettings != null
        applyBenefitsConfidenceFactor.value = model.ScheduleDependentSettings?.ApplyBenefitsConfidenceFactor ?? false
        valueCommitmentType.value = model.ScheduleDependentSettings?.ValueCommitmentType ?? ValueCommitmentType.Total

        defaultProjectStartDate.value = MonthYearHelpers.copyNullable(model.ScheduleDependentSettings?.DefaultProjectStartDate)
        startDateBoundary.value = MonthYearHelpers.copyNullable(model.ScheduleDependentSettings?.StartDateBoundary)
        endDateBoundary.value = MonthYearHelpers.copyNullable(model.ScheduleDependentSettings?.EndDateBoundary)

        initialized.value = true
    })

    onSucceedCreateExpenseMetricCommandV1(() =>
    {
        allowRouteLeave.value = true
        router.go(-1)
    })

    onSucceedUpdateExpenseMetricCommandV1(() =>
    {
        allowRouteLeave.value = true
        router.go(0)
    })

    watch(validationErrorsCreateExpenseMetricCommandV1, (validationErrors) =>
    {
        if (!validationErrors || validationErrors.length === 0) return
        errors.value = validationErrors
        isSaving.value = false
    })

    watch(validationErrorsUpdateExpenseMetricCommandV1, (validationErrors) =>
    {
        if (!validationErrors || validationErrors.length === 0) return
        errors.value = validationErrors
        isSaving.value = false
    })

    onBeforeRouteLeave((to, from, next) =>
    {
        if (allowRouteLeave.value)
        {
            next()
            return
        }

        if (hasChanges.value && !isSaving.value)
        {
            if (confirm('You have unsaved changes. Do you want to save them before leaving?'))
            {
                handleSave()
            }
            else
            {
                next()
            }
        } else
        {
            next()
        }
    })
</script>


<template>
    <div>
        <CorporateNavigation ActivePage="metrics" />

        <div v-if="initialized" class="container" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h6>
                        <a class="text-dark" @click="handleBack" data-testid="expense-metric-config-back">
                            <i class="fa fa-angle-double-left" aria-hidden="true"></i> Back
                        </a>
                    </h6>
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">{{pageHeading}}</h1>
                </div>

                <div class="col-lg-6 text-right align-self-end">
                    <button type="submit" form="metric-configuration-form" class="mr-1" :disabled="isSaving" data-testid="expense-metric-config-save"
                            :class="hasChanges ? 'btn btn-warning' : 'btn btn-outline-secondary'" style="padding: 0.7em 2em;">
                        {{ saveButtonText }}
                    </button>
                    <button v-if="hasChanges" type="button" class="btn btn-outline-secondary" style="padding: 0.7em 2em;" @click="handleCancel" data-testid="expense-metric-config-cancel">
                        Cancel
                    </button>
                </div>
            </div>

            <hr class="mb-5" />

            <form id="metric-configuration-form" @submit.prevent="handleSave" class="mb-4" data-testid="expense-metric-config-form">
                <div class="form-group mb-4">
                    <label>Metric Name:</label>
                    <input v-model.trim="name" type="text" class="form-control" required maxlength="100" minlength="2" data-testid="expense-metric-config-name" />
                    <div class="error">{{nameError}}</div>
                </div>

                <div class="form-group mb-4">
                    <label>Metric Description:</label>
                    <textarea v-model.trim="description" class="form-control" data-testid="expense-metric-config-description"></textarea>
                </div>

                <div class="form-row">
                    <div class="form-group mb-4 col">
                        <label for="revenue" class="form-label">Measured Expense:</label>
                        <select id="revenue" v-model="expenseAccountId" class="form-control" data-testid="expense-metric-config-expense">
                            <option :value="null">Total Expense</option>
                            <option v-for="r in rm.Expenses" :key="r.Id" :value="r.Id">{{r.Name}}</option>
                        </select>
                    </div>

                    <div class="form-group mb-4 col">
                        <label for="valueBalanceType" class="form-label">Data Type:</label>
                        <select id="valueBalanceType" v-model="valueBalanceType" class="form-control" data-testid="expense-metric-config-value-balance-type">
                            <option value="Costs">Costs</option>
                            <option value="Benefits">Savings</option>
                            <option value="Net">Balance (Costs - Savings)</option>
                        </select>
                    </div>
                </div>

                <div class="form-group mb-4">
                    <label for="denomination">Currency denomination:</label>
                    <select id="denomination" v-model="denomination" class="form-control" data-testid="expense-metric-config-denomination">
                        <option value="Unit">Unit</option>
                        <option value="Thousand">Thousand</option>
                        <option value="Million">Million</option>
                    </select>
                </div>

                <MetricTimeWindowSettings v-model:startOffset="windowStartOffsetInMonths"
                                          v-model:length="windowLengthInMonths" />

                <MetricScheduleDependentSettings :canDisable="true"
                                                 v-model:isScheduleDependent="isScheduleDependent"
                                                 v-model:defaultProjectStartDate="defaultProjectStartDate"
                                                 v-model:startDateBoundary="startDateBoundary"
                                                 v-model:endDateBoundary="endDateBoundary"
                                                 v-model:applyBenefitsConfidenceFactor="applyBenefitsConfidenceFactor"
                                                 v-model:valueCommitmentType="valueCommitmentType" />
            </form>
        </div>
    </div>
</template>
